import { useQuery } from '@tanstack/react-query';
import { CreditCard, Search, ShoppingCart, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { adminFetch } from '../lib/api';

/**
 * Global cross-entity search that lives in the top bar of every admin
 * page. Cmd+K / Ctrl+K opens it; Esc closes it.
 *
 * Hits the backend's `GET /admin/search?q=...` which returns up to
 * `per_entity_limit` (10) results per entity, spanning leads, orders,
 * and subscriptions. Picking a result navigates to the matching page
 * (currently anchored at the entity's list view; future work can deep-
 * link straight into the dialog).
 */

interface SearchHit {
  kind: 'lead' | 'order' | 'subscription';
  id: number;
  label: string;
  sublabel: string | null;
  lead_id: number | null;
  order_ref: string | null;
}

interface SearchResult {
  query: string;
  hits: SearchHit[];
}

const KIND_ICON: Record<SearchHit['kind'], React.ReactNode> = {
  lead: <User className="h-3.5 w-3.5 text-primary" />,
  order: <ShoppingCart className="h-3.5 w-3.5 text-blue-600" />,
  subscription: <CreditCard className="h-3.5 w-3.5 text-indigo-600" />,
};

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Cmd/Ctrl+K → open. Esc → close. Focus moves into the input on open.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
        setQ('');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const trimmed = q.trim();
  const search = useQuery({
    queryKey: ['admin', 'search', trimmed],
    queryFn: () => adminFetch<SearchResult>('/api/v1/admin/search', { query: { q: trimmed } }),
    enabled: trimmed.length >= 2,
    staleTime: 30_000,
  });

  function navigateToHit(hit: SearchHit) {
    setOpen(false);
    setQ('');
    // Anchor at the entity-type page. The query param tells the page
    // to open the matching detail dialog if it can; pages that don't
    // support it (yet) simply land on the list.
    if (hit.kind === 'lead') {
      navigate(`/leads?openLead=${hit.id}`);
    } else if (hit.kind === 'order') {
      navigate(`/orders?openOrder=${hit.id}`);
    } else {
      navigate(`/subscriptions?openSub=${hit.id}`);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        className="flex shrink-0 items-center gap-2 rounded-md border bg-background p-1.5 text-sm text-muted-foreground hover:bg-secondary sm:px-3"
        title="Global search (⌘K)"
      >
        <Search className="h-4 w-4" />
        <span className="hidden sm:inline">Search…</span>
        <kbd className="ml-2 hidden rounded border bg-muted px-1.5 py-0.5 text-[10px] font-mono sm:inline">
          ⌘K
        </kbd>
      </button>

      {open && (
        // Backdrop + centered modal. Click outside or Esc to close.
        <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-16 sm:pt-24">
          <button
            type="button"
            aria-label="Close search"
            onClick={() => setOpen(false)}
            className="absolute inset-0 cursor-default bg-black/40"
          />
          <div className="relative w-full max-w-xl overflow-hidden rounded-lg border bg-background shadow-2xl">
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search leads, orders, subscriptions… (email / ref / username)"
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
              <kbd className="rounded border px-1.5 py-0.5 text-[10px]">Esc</kbd>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {trimmed.length < 2 && (
                <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                  Type at least 2 characters to search.
                </p>
              )}
              {trimmed.length >= 2 && search.isPending && (
                <p className="px-3 py-6 text-center text-xs text-muted-foreground">Searching…</p>
              )}
              {search.data && search.data.hits.length === 0 && (
                <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                  No matches for &ldquo;{search.data.query}&rdquo;.
                </p>
              )}
              {search.data && search.data.hits.length > 0 && (
                <ul className="divide-y">
                  {search.data.hits.map((h) => (
                    <li key={`${h.kind}-${h.id}`}>
                      <button
                        type="button"
                        onClick={() => navigateToHit(h)}
                        className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-secondary"
                      >
                        <span className="flex h-7 w-7 items-center justify-center rounded-md border bg-card">
                          {KIND_ICON[h.kind]}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block truncate font-medium">{h.label}</span>
                          {h.sublabel && (
                            <span className="block truncate text-xs text-muted-foreground">
                              {h.sublabel}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
